import { computed, signal } from '@angular/core';
import type { Observable } from 'rxjs';
import { EMPTY, catchError, take } from 'rxjs';
import type { ApiState } from '@comparit/core';

/**
 * Manages the lifecycle of a user-triggered API call (button click, form submit, etc.).
 *
 * Encapsulates loading, success, and error state in a single `ApiState<T>` signal
 * and exposes convenience computed signals for templates.
 *
 * Usage:
 * ```ts
 * readonly download = new ApiAction<Blob>();
 *
 * onDownload(): void {
 *   this.download.execute(this.http.get('/api/doc', { responseType: 'blob' }));
 * }
 * ```
 *
 * Template:
 * ```html
 * <button [disabled]="download.loading()" (click)="onDownload()">
 *   @if (download.loading()) { <mat-spinner /> }
 * </button>
 * ```
 */
export class ApiAction<T = unknown> {
  readonly state = signal<ApiState<T> | null>(null);

  readonly loading = computed(() => this.state()?.state === 'loading');
  readonly error = computed(() => {
    const s = this.state();
    return s?.state === 'error' ? s.error : null;
  });
  readonly data = computed(() => {
    const s = this.state();
    return s?.state === 'success' ? s.data : null;
  });

  /**
   * Executes the given observable as an async action.
   * Sets state to loading, then success or error on completion.
   * No-ops if already loading (prevents double-submit).
   *
   * @param action$ The observable to execute (taken once automatically).
   * @param onError Optional error mapper. Receives the raw error and returns a user-facing message.
   *                Defaults to `'Ein unerwarteter Fehler ist aufgetreten.'`.
   */
  execute(action$: Observable<T>, onError?: (err: unknown) => string): void {
    if (this.loading()) return;

    this.state.set({ state: 'loading' });

    action$
      .pipe(
        take(1),
        catchError((err: unknown) => {
          const message = onError ? onError(err) : 'Ein unerwarteter Fehler ist aufgetreten.';
          this.state.set({ state: 'error', error: message });
          return EMPTY;
        })
      )
      .subscribe(data => {
        this.state.set({ state: 'success', data });
      });
  }

  reset(): void {
    this.state.set(null);
  }
}
